import { logger } from '@beelday/common'
import { UUID } from 'common/types'
import { UserKnowledgeCheckResults } from './knowledge-check'
import useKnowledgeChecks from './use-knowledge-checks'
import { useCallback, useEffect, useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'

export type KnowledgeCheckResults = {
	results?: UserKnowledgeCheckResults
	error: boolean
	reload: () => void
}

/**
 * The participant's own results of a finished check: the score and, per
 * question, the options with the ones they picked and whether each was right.
 * Shared by the desktop result card and the in-person phone.
 */
export const useKnowledgeCheckResults = (
	knowledgeCheckId: UUID
): KnowledgeCheckResults => {
	const roomAddress = useAssertedJoinedRoomAddress()
	const { getUserResults } = useKnowledgeChecks()
	const [results, setResults] = useState<UserKnowledgeCheckResults>()
	const [error, setError] = useState(false)
	const [attempt, setAttempt] = useState(0)

	useEffect(() => {
		let current = true
		setError(false)
		getUserResults(roomAddress, knowledgeCheckId)
			.then(loaded => {
				if (current) setResults(loaded)
			})
			.catch(error => {
				logger.error('Failed to fetch knowledge check results', error)
				if (current) setError(true)
			})
		return () => {
			current = false
		}
	}, [getUserResults, roomAddress, knowledgeCheckId, attempt])

	const reload = useCallback(() => setAttempt(value => value + 1), [])

	return { results, error, reload }
}
